import { useCallback, useEffect, useRef, useState } from "react";
import { getAccessToken, getRefreshToken, getUserMetadata } from "./authTokenStore.js";
import {
    DIRECTORY_OAUTH_RESULT,
    DIRECTORY_OAUTH_SESSION_RESPONSE,
    createDirectoryOauthPending,
    currentDirectoryOauthBinding,
    isDirectoryOauthMessage,
    isDirectoryOauthRequest,
    sameAuthBinding,
} from "./directoryOauthHandshake.js";

export const POPUP_CLOSE_POLL_MS = 800;
export const POPUP_RESULT_GRACE_MS = 1_500;

export function useDirectoryOauthPopup({ onResult, onClosedWithoutResult }) {
    const [pendingId, setPendingId] = useState(null);
    const pendingRef = useRef(null);
    const popupRef = useRef(null);
    const onResultRef = useRef(onResult);
    const onClosedRef = useRef(onClosedWithoutResult);
    useEffect(() => {
        onResultRef.current = onResult;
        onClosedRef.current = onClosedWithoutResult;
    }, [onResult, onClosedWithoutResult]);

    const clear = useCallback(() => {
        pendingRef.current = null;
        popupRef.current = null;
        setPendingId(null);
    }, []);

    const start = useCallback(({ integrationId, provider, redirectUri, authUrl }) => {
        const pending = createDirectoryOauthPending({ integrationId, provider, redirectUri });
        pendingRef.current = pending;
        const popup = window.open(authUrl, pending.windowName);
        if (!popup) {
            pendingRef.current = null;
            return false;
        }
        popupRef.current = popup;
        setPendingId(integrationId);
        return true;
    }, []);

    const cancel = useCallback(() => {
        const id = pendingRef.current?.integrationId ?? null;
        clear();
        return id;
    }, [clear]);

    useEffect(() => {
        const handler = (event) => {
            const pending = pendingRef.current;
            const popup = popupRef.current;
            if (!pending || !popup) return;

            if (isDirectoryOauthRequest(event, { source: popup, requestId: pending.requestId })) {
                const access = getAccessToken();
                if (!access || !sameAuthBinding(currentDirectoryOauthBinding(), pending.binding)) return;
                event.source.postMessage({
                    type: DIRECTORY_OAUTH_SESSION_RESPONSE,
                    requestId: pending.requestId,
                    authBinding: pending.binding,
                    access,
                    refresh: getRefreshToken(),
                    user: getUserMetadata(),
                    pending: {
                        integrationId: pending.integrationId,
                        provider: pending.provider,
                        redirectUri: pending.redirectUri,
                        requestId: pending.requestId,
                        binding: pending.binding,
                    },
                }, window.location.origin);
                return;
            }

            if (!isDirectoryOauthMessage(event, {
                source: popup,
                requestId: pending.requestId,
                binding: pending.binding,
                type: DIRECTORY_OAUTH_RESULT,
            })) return;

            clear();
            onResultRef.current?.({
                integrationId: pending.integrationId,
                status: event.data.status,
                message: event.data.message,
            });
        };
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, [clear]);

    useEffect(() => {
        if (!pendingId) return undefined;
        const popup = popupRef.current;
        if (!popup) return undefined;
        let graceHandle = null;
        const pollHandle = window.setInterval(() => {
            if (!popup.closed) return;
            window.clearInterval(pollHandle);
            graceHandle = window.setTimeout(() => {
                const id = pendingRef.current?.integrationId ?? pendingId;
                clear();
                onClosedRef.current?.(id);
            }, POPUP_RESULT_GRACE_MS);
        }, POPUP_CLOSE_POLL_MS);
        return () => {
            window.clearInterval(pollHandle);
            if (graceHandle) window.clearTimeout(graceHandle);
        };
    }, [pendingId, clear]);

    return { pendingId, start, cancel };
}
